import { useState, useEffect } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useRole } from "@/contexts/RoleContext";
import { useClient } from "@/contexts/ClientContext";
import {
  LayoutDashboard,
  Building2,
  DollarSign,
  LogOut,
  Package,
  Users,
  CreditCard,
  Menu,
  X,
} from "lucide-react";
import { ClientSwitcher } from "./ClientSwitcher";
import { InstallationIndicator } from "./InstallationIndicator";
import { WelcomeModal } from "./WelcomeModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GuidedTour } from "./GuidedTour";
import { useNavigate } from "react-router-dom";

export function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { role, isPartner, isClient, isSolo } = useRole();
  const { clients } = useClient();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && sidebarOpen) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [sidebarOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const NavLink = ({ to, icon: Icon, children, onClick }: { to: string; icon: React.ElementType; children: React.ReactNode; onClick?: () => void }) => {
    const isActive = location.pathname === to ||
      (to === "/partner/dashboard" && location.pathname === "/") ||
      (to === "/client/dashboard" && location.pathname === "/") ||
      (to === "/solo/dashboard" && location.pathname === "/") ||
      (to === "/client/instances" && location.pathname.startsWith("/client/instances")) ||
      (to === "/solo/instances" && location.pathname.startsWith("/solo/instances"));

    return (
      <Link
        to={to}
        onClick={onClick}
        className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
            : "text-slate-400 hover:bg-slate-700/50 hover:text-slate-300"
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="font-medium">{children}</span>
      </Link>
    );
  };

  const SidebarContent = ({ onLinkClick }: { onLinkClick?: () => void }) => (
    <>
      <nav className="p-4 space-y-2">
        {/* Partner Navigation */}
        {isPartner && (
          <>
            <div data-tour="dashboard">
              <NavLink to="/partner/dashboard" icon={LayoutDashboard} onClick={onLinkClick}>
                Dashboard
              </NavLink>
            </div>
            <div data-tour="billing">
              <NavLink to="/partner/billing" icon={DollarSign} onClick={onLinkClick}>
                Billing
              </NavLink>
            </div>
          </>
        )}

        {/* Client Navigation */}
        {isClient && (
          <>
            <div data-tour="dashboard">
              <NavLink to="/client/dashboard" icon={LayoutDashboard} onClick={onLinkClick}>
                Dashboard
              </NavLink>
            </div>
            <div data-tour="instances">
              <NavLink to="/client/instances" icon={Package} onClick={onLinkClick}>
                My Instances
              </NavLink>
            </div>
            <div data-tour="subscription">
              <NavLink to="/client/subscription" icon={CreditCard} onClick={onLinkClick}>
                Subscription
              </NavLink>
            </div>
            <div data-tour="users">
              <NavLink to="/client/users" icon={Users} onClick={onLinkClick}>
                Users
              </NavLink>
            </div>
          </>
        )}

        {/* Solo User Navigation - Simplified for direct customers */}
        {isSolo && (
          <>
            <div data-tour="dashboard">
              <NavLink to="/solo/dashboard" icon={LayoutDashboard} onClick={onLinkClick}>
                Dashboard
              </NavLink>
            </div>
            <div data-tour="instances">
              <NavLink to="/solo/instances" icon={Package} onClick={onLinkClick}>
                My Instances
              </NavLink>
            </div>
            <div data-tour="billing">
              <NavLink to="/solo/billing" icon={CreditCard} onClick={onLinkClick}>
                Billing
              </NavLink>
            </div>
          </>
        )}
      </nav>

      {/* Partner Info */}
      {isPartner && (
        <div className="p-4 mt-8">
          <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600">
            <div className="text-xs font-medium text-slate-500 mb-2">
              PARTNER INFO
            </div>
            <div className="space-y-1">
              <div className="text-sm text-slate-300">
                Tier:{" "}
                <span className="font-semibold text-emerald-400">
                  {user?.partner?.tier || user?.partner_tier}
                </span>
              </div>
              <div className="text-sm text-slate-300">
                Clients:{" "}
                <span className="font-semibold">{clients.length}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Top Navigation */}
      <nav className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-14 sm:h-16">
            {/* Mobile Menu Button + Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden p-2 -ml-2 rounded-lg text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
                aria-label={sidebarOpen ? "Close menu" : "Open menu"}
                aria-expanded={sidebarOpen}
                aria-controls="mobile-sidebar"
              >
                {sidebarOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>

              {/* Logo */}
              <p className="text-lg sm:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400" aria-label="AIIA Portal">
                <span className="hidden sm:inline">AIIA {isPartner ? "Partner" : isClient ? "Client" : isSolo ? "" : ""} Portal</span>
                <span className="sm:hidden">AIIA</span>
              </p>

              {/* Client Switcher - Hidden on mobile, shown in sidebar instead */}
              {isPartner && (
                <>
                  <div className="hidden md:block h-6 w-px bg-slate-700" />
                  <div className="hidden md:block" data-tour="client-switcher">
                    <ClientSwitcher />
                  </div>
                </>
              )}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Installation Indicator */}
              <div data-tour="installation-indicator">
                <InstallationIndicator
                  onViewDetails={(instanceId) => {
                    navigate(`/client/instances/${instanceId}`);
                  }}
                />
              </div>

              {/* User Info - Condensed on mobile */}
              <div className="text-sm hidden sm:block">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-300 max-w-[150px] truncate">
                    {user?.full_name || user?.name || user?.email}
                  </span>
                  {role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 capitalize">
                      {role}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate max-w-[200px]">
                  {user?.partner?.name || user?.partner_name}
                </div>
              </div>

              {/* Logout Button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={logout}
                      className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                      aria-label="Logout"
                    >
                      <LogOut className="h-5 w-5 text-slate-400" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Sign out</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex relative">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar - Desktop: always visible, Mobile: drawer */}
        <aside
          id="mobile-sidebar"
          className={`
            fixed md:sticky top-14 sm:top-16 left-0 z-50 md:z-0
            w-64 bg-slate-800/95 md:bg-slate-800/30
            border-r border-slate-700
            min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)]
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          `}
          aria-label="Main navigation"
        >
          {/* Mobile-only: Client Switcher at top of sidebar */}
          {isPartner && (
            <div className="md:hidden p-4 border-b border-slate-700">
              <div className="text-xs font-medium text-slate-500 mb-2">
                SWITCH CLIENT
              </div>
              <ClientSwitcher />
            </div>
          )}

          <SidebarContent onLinkClick={() => setSidebarOpen(false)} />
        </aside>

        {/* Main Content */}
        <main
          id="main-content"
          role="main"
          className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0"
        >
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* First-time user welcome modal */}
      <WelcomeModal onStartTour={() => setRunTour(true)} />

      {/* Guided tour */}
      <GuidedTour run={runTour} onComplete={() => setRunTour(false)} />
    </div>
  );
}
